import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { useSystem } from "@/utils/hooks/useLocation";
import { downloadFile, filterObject } from "@/utils/helpers";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

export const useGetPilgrimQuery = (id, queryOptions = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem()
  let system = currentSystem === "makkah" ? "makkah" : "madina";
  return useQuery({
    queryKey: ["pilgrim", system, id],
    queryFn: ({ signal }) =>
      axiosProtected.get(`${system}/detailed-pilgrims/${id}`, { signal }),
    ...queryOptions,
  });
};

export const useGetPilgrimsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["pilgrims", params],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/detailed-pilgrims/list`, { params }),
  });
};

export const useCreatePilgrimPassportReceiptMutation = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ pilgrimId, payload }) =>
      axiosProtected.post(
        `${currentSystem}/detailed-pilgrims/${pilgrimId}/passport-receipt`,
        filterObject(payload)
      ),
    onSuccess: (_res, variables) => {
      toast.success(t("PASSPORT_RECEIPT_REGISTERED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["pilgrims"] });
      queryClient.invalidateQueries({
        queryKey: ["pilgrim", currentSystem, variables?.pilgrimId],
      });
    },
  });
};

export const usePrintPilgrimPassportReceiptMutation = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ pilgrimId, payload = {} }) =>
      axiosProtected.post(
        `${currentSystem}/detailed-pilgrims/${pilgrimId}/passport-receipt/print`,
        payload,
        { responseType: "blob" }
      ),
    onSuccess: (res, variables) => {
      const header = res?.headers?.["content-disposition"] ?? "";
      const match = /filename="?([^"]+)"?/i.exec(header);
      const filename = match?.[1] || `passport-receipt-${variables?.pilgrimId}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });
      downloadFile(blob, filename);
    },
    onError: async (err) => {
      let message = t("SOMETHING_WENT_WRONG");
      const data = err?.response?.data;

      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.message) message = parsed.message;
        } catch {
          /* keep fallback */
        }
      } else if (err?.response?.data?.message) {
        message = err.response.data.message;
      }

      toast.error(message);
    },
  });
};
